/**
 * Generic Facebook service that wraps
 * https://developers.facebook.com/docs/javascript/reference/v3.2
 *
 * Usage:
 * FacebookService.init([settings], function(err){
 *     if (err) { return; }
 *     FacebookService.authorize([permissions], function(err, res) {
 *         if (err) { return; }
 *         // user is authorized and has permissions, do something with `res`
 *     });
 * });
 */

window.FacebookServiceSettings = {
    locale: 'en_US',
    init: {
        version: 'v10.0',
        appId: window.facebookAppId,
        status: true, // check login status
        cookie: true, // enable cookies to allow the server to access the session
        xfbml: false,  // parse XFBML
        oauth: true,
        channelUrl: window.facebookChannelUrl
    },
    permissions: [
        'email',
        'public_profile'
    ]
};

window.FacebookService = (function(win, doc) {

    var injected = false;
    var userPermissions = [];
    var initialStatus;
    var publicApi = {
        authorize: authorize,
        getProfile: getProfile,
        hasPermissions: hasPermissions,
        init: init,
        injectScript: injectScript,
        login: login,
        shareDialog: shareDialog
    };

    /**
     * Inject the facebook sdk. To change the locale, set `FacebookServiceSettings.locale`
     * before calling this method.
     * @method FacebookService.injectScript
     * @param {Function} [callback] - Will be called with err or null.
     */

    function injectScript (callback) {
        var script;
        if (injected) {
            return;
        }
        callback = getCallback(callback);

        script = doc.createElement('script');
        script.src = '//connect.facebook.net/' + window.FacebookServiceSettings.locale + '/sdk.js';
        script.id = 'facebook-jssdk';
        script.async = true;
        script.onload = function() {
            callback();
        };
        script.onerror = function(err) {
            callback(err);
        };

        doc.getElementsByTagName('head')[0].appendChild(script);
        injected = true;
    }

    /**
     * Call FB.init and fetch permissions when connected. This will call
     * FacebookService.injectScript if necessary.
     * @method FacebookService.init
     * @param {Object} [settings] - Settings passed to FB.init. Will be merged
     * with FacebookServiceSettings.init. Available settings can be found here:
     * https://developers.facebook.com/docs/javascript/reference/FB.init/v3.2
     * @param {Function} [callback] - Will be called with err or null.
     */

    function init (settings, callback) {
        var initFacebook = function() {
            win.FB.Event.subscribe('auth.statusChange', function(res) {
                initialStatus = res;
                if (res.status === 'connected' && !userPermissions.length) {
                    win.FB.api('/me/permissions', function(pRes) {
                        userPermissions = (pRes && pRes.data) ? pRes.data : [];
                    });
                }
            });
            try {
                win.FB.init(settings);
                callback();
            }
            catch (err) {
                callback(err);
            }
        };
        callback = getCallback(settings, callback);
        if (typeof settings !== 'object') {
            settings = {};
        }
        settings = merge(window.FacebookServiceSettings.init, settings);

        if (!win.FB) {
            injectScript(function() {
                initFacebook();
            });
        }
        else {
            initFacebook();
        }
    }

    /**
     * Call FB.login with requested permissions found at
     * https://developers.facebook.com/docs/facebook-login/permissions/v3.2
     * @method FacebookService.login
     * @param {string[]} [permissions] - Array of permissions. Defaults to
     * `FacebookServiceSettings.permissions`.
     * @param {Function} [callback] - Will be called with err and the response
     * from facebook which looks something like:
     * ```
     * {
     *      status: 'connected',
     *      authResponse: {
     *          accessToken: '...',
     *          expiresIn:'...',
     *          signedRequest:'...',
     *          userID:'...'
     *      }
     * }
     * ```
     * @param {Boolean} [rerequest] - Should permissions be rerequested? Use this
     * if user has already authorized but chose to decline some permissions.
     */

    function login (permissions, callback, rerequest) {
        if (!win.FB) {
            return notInjectedWarning();
        }
        var options = {
            return_scopes: true
        };
        callback = getCallback(permissions, callback);
        permissions = getPermissions(permissions);
        options.scope = permissions.join(',');

        if (rerequest) {
            options.auth_type = 'rerequest';
        }
        win.FB.login(function(res) {
            if (res.authResponse) {
                if (res.authResponse.grantedScopes) {
                    userPermissions = res.authResponse.grantedScopes.split(',').map(function(permission) {
                        return {
                            permission: permission,
                            status: 'granted'
                        };
                    });
                }
                return callback(null, res);
            }

            callback(new Error('Not authorized.'));
        }, options);
    }

    /**
     * Check if user has requested permissions. User permissions should already
     * have been fetched by calling `FacebookService.init` before calling this
     * function.
     * @method FacebookService.hasPermissions
     * @param {string[]} [permissions] - Array of permissions. Defaults to
     * `FacebookServiceSettings.permissions`.
     * @param {boolean} [checkGranted] - Check if user has also granted every
     * permission since the user has the ability to choose specific permissions.
     * @return {boolean}
     */

    function hasPermissions (permissions, checkGranted) {
        if (!win.FB) {
            return notInjectedWarning();
        }
        permissions = getPermissions(permissions);
        if (userPermissions.length) {
            return permissions.every(function(permission) {
                return userPermissions.some(function(userPermission) {
                    var isGranted = true;
                    if (checkGranted) {
                        isGranted = (userPermission.status === 'granted');
                    }
                    return (permission === userPermission.permission && isGranted);
                });
            });
        }

        return false;
    }

    /**
     * Check permissions and login if necessary. This will also check to make
     * sure all permissions are granted. If not, it will try to reauthorize.
     * @method FacebookService.authorize
     * @param {string[]} [permissions] - Array of permissions. Defaults to
     * `FacebookServiceSettings.permissions`.
     * @param {Function} [callback] - Will be called with err and/or response
     * from Facebook.
     */

    function authorize (permissions, callback) {
        var rerequest = (hasPermissions(permissions) && !hasPermissions(permissions, true));
        callback = getCallback(permissions, callback);
        permissions = getPermissions(permissions);

        if (!hasPermissions(permissions) || rerequest) {
            login(permissions, callback, rerequest);
        }
        else {
            callback(null, initialStatus);
        }
    }

    /**
     * Get the user profile, calling the /me graph api endpoint.
     * @method FacebookService.getProfile
     * @param {string[]} [fields] - Array of fields to fetch.
     * @param {Function} [callback] - Will be called with err and/or response
     * from Facebook.
     */

    function getProfile (fields, callback) {
        var options = {};
        callback = getCallback(fields, callback);
        if (typeof fields === 'object') {
            options.fields = fields.join(',');
        }
        win.FB.api('/me', options, function(res) {
            var error = (res && res.error) ? res.error : 'Unknown error.';
            if (!res || res.error) {
                return callback(new Error(error));
            }
            callback(null, res);
        });
    }

    function shareDialog (urlToShare, message) {
        win.FB.ui({
            method: 'share',
            display: 'popup',
            href: urlToShare,
            quote: message
            }, function(response){
            });
    }

    function notInjectedWarning () {
        console.warn('Facebook script has not been injected!');
    }

    /** Return provided permissions or default permissions. */
    function getPermissions (permissions) {
        if (!Array.isArray(permissions)) {
            return window.FacebookServiceSettings.permissions;
        }
        return permissions;
    }

    /** Find a callback given optional parameters. */
    function getCallback (callback) {
        var i;
        if (arguments.length > 1) {
            for(i = 0; i < arguments.length; i++) {
                if (typeof arguments[i] === 'function') {
                    return arguments[i];
                }
            }
        }
        if (typeof callback !== 'function') {
            return function() {};
        }
        return callback;
    }

    function merge(obj1, obj2){
        var obj3 = {};
        var attr;
        for (attr in obj1) {
            obj3[attr] = obj1[attr];
        }
        for (attr in obj2) {
            obj3[attr] = obj2[attr];
        }
        return obj3;
    }

    /** Add angular module and service if angular is available. */
    if (win.angular) {
        win.angular.module('jv.FacebookService', []).factory('jvFacebookService', function(){
            return publicApi;
        });
    }

    return publicApi;

})(window, document);